import { useState, type FormEvent, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Banknote,
  CreditCard,
  ClipboardList,
  DownloadCloud,
  GaugeCircle,
  Globe2,
  LogOut,
  PackageCheck,
  Search,
  ShieldCheck,
  Users
} from "lucide-react";
import { useAdminAuth } from "../auth/useAdminAuth.js";
import { cn } from "../lib/cn.js";
import { Button } from "../components/ui/Button.js";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: "工作台",
    items: [{ to: "/", label: "运营工作台", icon: <GaugeCircle className="h-4 w-4" /> }]
  },
  {
    title: "客户运营",
    items: [{ to: "/tenants", label: "客户管理", icon: <Users className="h-4 w-4" /> }]
  },
  {
    title: "订单与套餐",
    items: [
      { to: "/billing/orders", label: "订单管理", icon: <Banknote className="h-4 w-4" /> },
      { to: "/billing/settings", label: "套餐与支付", icon: <CreditCard className="h-4 w-4" /> }
    ]
  },
  {
    title: "发布与采集",
    items: [
      { to: "/rule-bundles", label: "规则包", icon: <PackageCheck className="h-4 w-4" /> },
      { to: "/system/public-collect-proxy", label: "采集代理池", icon: <Globe2 className="h-4 w-4" /> },
      { to: "/desktop-updates", label: "桌面更新", icon: <DownloadCloud className="h-4 w-4" /> }
    ]
  },
  {
    title: "审计与系统",
    items: [{ to: "/audit", label: "审计日志", icon: <ClipboardList className="h-4 w-4" /> }]
  }
];

export function AdminShell() {
  const { session, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (search.trim().length > 0) {
      navigate("/tenants");
    }
  };

  return (
    <div className="grid h-full min-h-screen grid-cols-[248px_minmax(0,1fr)] bg-surface-base">
      <aside className="sticky top-0 flex h-screen flex-col border-r border-surface-border bg-surface-panel">
        <div className="flex h-16 items-center border-b border-surface-border px-4">
          <Link to="/" className="flex min-w-0 items-center gap-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded bg-text-primary text-xs font-semibold text-white">
              SD
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-text-primary">数店云端管理</span>
              <span className="block truncate text-[11px] text-text-muted">Cloud Admin Console</span>
            </span>
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {NAV_GROUPS.map((group) => (
            <section key={group.title} className="mb-4">
              <div className="px-2 pb-2 text-[11px] font-semibold text-text-muted">{group.title}</div>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      cn(
                        "flex h-9 items-center gap-2.5 rounded px-2.5 text-sm transition-colors",
                        isActive
                          ? "bg-accent-subtle font-medium text-accent"
                          : "text-text-secondary hover:bg-surface-raised hover:text-text-primary"
                      )
                    }
                  >
                    <span className="grid h-5 w-5 place-items-center">{item.icon}</span>
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </section>
          ))}
        </nav>
        <div className="border-t border-surface-border px-3 py-3">
          <div className="rounded border border-surface-border bg-surface-raised px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded bg-surface-mute text-xs font-semibold text-text-secondary">
                {(session?.admin.name ?? session?.admin.email ?? "管").slice(0, 1)}
              </span>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-text-primary" title={session?.admin.email}>
                  {session?.admin.name ?? session?.admin.email ?? "-"}
                </div>
                <div className="truncate text-xs text-text-muted" title={session?.admin.email}>
                  {session?.admin.email}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full justify-center"
              onClick={() => {
                void logout();
              }}
            >
              <LogOut className="h-3.5 w-3.5" />
              登出
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex h-full min-h-screen flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between gap-4 border-b border-surface-border bg-surface-panel/95 px-6 text-sm">
          <form
            className="flex h-9 w-full max-w-xl items-center gap-2 rounded border border-surface-border bg-surface-raised px-3"
            onSubmit={submitSearch}
          >
            <Search className="h-4 w-4 shrink-0 text-text-muted" />
            <input
              className="min-w-0 flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-muted"
              placeholder="搜索客户名 / 邮箱 / 客户 ID / 订单号 / 设备 ID"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </form>
          <div className="flex shrink-0 items-center gap-3">
            <span className="inline-flex h-8 items-center gap-1.5 rounded border border-status-success/25 bg-status-success/10 px-2.5 text-xs font-medium text-status-success">
              <ShieldCheck className="h-3.5 w-3.5" />
              生产环境正常
            </span>
            <Breadcrumbs path={location.pathname} />
          </div>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Breadcrumbs({ path }: { path: string }) {
  const titleByPath: Record<string, string> = {
    "/": "工作台 / 运营工作台",
    "/tenants": "客户运营 / 客户管理",
    "/tenants/new": "客户运营 / 新增客户",
    "/billing/orders": "订单与套餐 / 订单管理",
    "/billing/settings": "订单与套餐 / 套餐与支付",
    "/system/public-collect-proxy": "发布与采集 / 采集代理池",
    "/rule-bundles": "发布与采集 / 规则包",
    "/desktop-updates": "发布与采集 / 桌面更新",
    "/audit": "审计与系统 / 审计日志"
  };
  let title = titleByPath[path];
  if (!title && path.startsWith("/tenants/")) {
    title = "客户运营 / 客户详情";
  }
  if (!title) title = path;
  return <span className="hidden text-xs text-text-secondary lg:inline">{title}</span>;
}
